// R-112: resolve a notification/alarm's (resource_type, resource_id) to the
// in-app route that shows it — the "actionable" part of a notification. Returns
// null when the type has no dedicated destination (the row is then non-clickable).
// Detail routes carry the id via `href(ROUTES.x, { id })`; list routes don't.
// Reused by the Notifications inbox and the SOC alarm views.
import { ROUTES, href } from '../routes';

export function resourceHref(resourceType?: string | null, resourceId?: string | null): string | null {
  if (!resourceType) return null;
  const id = resourceId ?? undefined;
  switch (resourceType) {
    // R-173: both of these now carry the id through to the RECORD, not just the
    // screen that lists it. Each destination fetches the row by id, so the link
    // works for any age of alarm/incident and regardless of the filters the
    // target page happens to be showing.
    case 'alarm':
      return id ? href(ROUTES.soc, { alarm: id }) : ROUTES.soc;
    case 'incident':
      // R-126: incident detail lives in the site console, as an expanded row.
      return id ? href(ROUTES.maintenanceIncidents, { id }) : ROUTES.maintenanceIncidents;
    // ⚠ `batch`, `task` and `ai_scan` fall through to `null` now — Production,
    // Workforce and AI Monitoring were removed, so there is no page to send a
    // notification to and the row is correctly non-clickable rather than a link
    // into a 404. Restore the cases with the screens.
    // R-353 — the two resources a `cultivation.*` notification carries. A crop
    // alert points at the PLAN when it is about the cycle (a stage, a harvest, a
    // status change) and at the ROOM when it is about the climate; a room has no
    // record page of its own, so its destination is the Alerts list pre-filtered
    // to it, which is where the reader can see the rest of the story.
    case 'plan':
      return id ? href(ROUTES.planningPlanRecord, { id }) : ROUTES.planningPlans;
    case 'room':
      return id ? href(ROUTES.planningAlerts, { room: id }) : ROUTES.planningAlerts;
    case 'asset':
      return id ? href(ROUTES.assetsRecord, { id }) : ROUTES.assets;
    case 'asset_type':
      return ROUTES.assets;
    case 'tour':
      return id ? href(ROUTES.logisticsTour, { id }) : ROUTES.logistics;
    case 'sales_order':
      return ROUTES.sales;
    // R-234: a request now has a real record page of its own, so an id addresses
    // the record directly rather than a tab + row.
    case 'purchase_requisition':
      return id ? href(ROUTES.procurementRequestRecord, { id }) : ROUTES.procurementRequests;
    // An order still has no page of its own — the orders LIST expands each row
    // into its detail, so an id has nothing to address. Deliberately not passed:
    // a param nothing consumes implies a destination that does not exist.
    case 'purchase_order':
      return ROUTES.procurementOrders;
    case 'stock_item':
      return ROUTES.warehouse;
    case 'device':
      return ROUTES.devicesMicrocontrollers;
    default:
      return null;
  }
}
